/**
 * AuthContext — stores the admin JWT and restores it across page refreshes.
 *
 * The token is persisted in localStorage so a refresh (or reopening the browser)
 * keeps the admin logged in until the token expires (8 h, enforced server-side)
 * or they log out. An expired/invalid token is cleared automatically on the first
 * 401 from authFetch, which sends the UI back to the login screen.
 *
 * Exposes:
 *   - token: string | null
 *   - login(username, password): Promise<void>  -> throws on failure
 *   - logout(): void
 *   - authFetch(input, init): Promise<Response>  -> auto-attaches Bearer + handles 401
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'brazo_admin_session';

function _loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, username: null };
    const s = JSON.parse(raw);
    return { token: s.token || null, username: s.username || null };
  } catch {
    return { token: null, username: null };
  }
}

function _saveSession(token, username) {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, username }));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage no disponible (modo privado, etc.) — la sesión vive solo en memoria */
  }
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => _loadSession().token);
  const [username, setUsername] = useState(() => _loadSession().username);

  const login = useCallback(async (user, pass) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: user, password: pass }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Error al iniciar sesión';
      throw new Error(msg);
    }
    setToken(data.token);
    setUsername(data.username);
    _saveSession(data.token, data.username);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUsername(null);
    _saveSession(null, null);
  }, []);

  const authFetch = useCallback(async (input, init = {}) => {
    const headers = new Headers(init.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(input, { ...init, headers });
    if (res.status === 401) {
      // Token expired or invalid -> force logout so UI shows login screen
      setToken(null);
      setUsername(null);
      _saveSession(null, null);
    }
    return res;
  }, [token]);

  const value = useMemo(
    () => ({ token, username, isAuthenticated: !!token, login, logout, authFetch }),
    [token, username, login, logout, authFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
